import { useState } from "react";
import type { ReactElement } from "react";
import { Abschnitt } from "@kundeninfo/ui/me-design";
import { renderMarkdown } from "../markdown.js";

/**
 * Einrichtungshilfe für die Fremdsysteme GitLab und YouTrack (Paket 1g, Dirk 20.10.2026).
 *
 * Sie steht an ZWEI Stellen der Schnittstellen-Ansicht: als eigener Eintrag „Fremdsysteme
 * einrichten" (sichtbar auch ohne eingerichtete Instanz — gerade dann braucht man sie) und
 * im Detail jeder GitLab-/YouTrack-Zeile des Servers. Der Text ist EINE Markdown-Quelle,
 * gezeichnet mit dem sanitisierenden Renderer der App (`renderMarkdown`): Überschriften,
 * nummerierte Schritte und Codeblöcke ohne eigenes Markup, und eine Änderung des Textes
 * ist eine Änderung an genau einer Stelle.
 *
 * Er nennt Pfade, Rollen und Aufrufe, aber nie ein Token, eine Adresse mit Zugangsdaten oder
 * ein Token-Beispiel: Die Ansicht ist für JEDEN Angemeldeten offen. Was hier steht, steht
 * ausführlicher in deploy/README.md, Abschnitt „Tickets Nachtrag 1f".
 */

/** Der feste Pfad des Einrichtungsskripts im Repo (seit Paket 1g). */
export const EINRICHTUNGSSKRIPT = "deploy/verknuepfungen-einrichten.sh";

/** Die Kennung des Hilfe-Eintrags in der Dienste-Spalte (Adresse `/schnittstellen/fremdsysteme`). */
export const FREMDSYSTEME_SCHLUESSEL = "FREMDSYSTEME";
export const FREMDSYSTEME_NAME = "Fremdsysteme einrichten";

/** Gehört eine Dienstzeile des Servers zu den Ticket-Instanzen (`tickets`,
 *  `tickets-gitlab-<key>`, `tickets-youtrack-<key>`, siehe api/src/routes/schnittstellen.ts)? */
export function istTicketDienst(schluessel: string): boolean {
  return schluessel === "tickets" || schluessel.startsWith("tickets-");
}

export const FREMDSYSTEME_HILFE = `#### Was ein Dienstkonto ist

Das Kundeninfo liest GitLab und YouTrack über je ein **Dienstkonto**: einen technischen Benutzer, der keiner Person gehört und nicht mit ihr geht. Alle Abrufe laufen über ihn; die Nutzer ordnen nur ihr eigenes Konto zu.

> [!WARNUNG] Token nur auf dem Server
> Das Token des Dienstkontos steht ausschliesslich in \`/srv/kundeninfo/kundeninfo.config.json\` (Rechte 600), Abschnitt \`schnittstellen.gitlab\` bzw. \`schnittstellen.youtrack\`, im Eintrag mit dem \`key\` der Instanz. Nie ins Repository, in die Datenbank, in einen Chat oder in eine Mail.

#### GitLab

1. Im Admin-Bereich (Admin › Benutzer › Neuer Benutzer) den Benutzer \`kundeninfo\` anlegen — **ohne** Administratorrecht (Entscheid 230 b).
2. Ihn in den Gruppen, deren Vorgänge im Kundeninfo erscheinen sollen, als Mitglied mit der Rolle **Reporter** aufnehmen — nur dort, denn jeder Angemeldete findet über die Suche, was dieses Konto lesen darf.
3. Als \`kundeninfo\` angemeldet unter Profil › Zugriffstoken (Personal Access Token) ein Token mit dem Scope \`read_api\` erzeugen. Den Scope \`api\` erst, wenn das Rückschreiben freigegeben ist.
4. Das Ablaufdatum notieren und vor Ablauf ein neues Token erzeugen.

Ohne Administratorrecht nennt GitLab die E-Mail der Konten nicht. Deshalb finden Nutzer ihr GitLab-Konto nicht selbst — die Verwaltung ordnet es zu (unten).

#### YouTrack

1. Den Benutzer \`kundeninfo\` anlegen.
2. In den Projekten, deren Vorgänge erscheinen sollen, die Rolle **Observer** (nur lesen) bzw. **Reporter** geben.
3. Zusätzlich die Berechtigung **Read User Full** — sonst fehlt in der Benutzerliste die E-Mail.
4. Später, fürs Rückschreiben, **Update Issue** und für Aufwände **Update Work Item**.
5. Als \`kundeninfo\` angemeldet unter Profil › Kontosicherheit › Token ein Token mit dem Scope **YouTrack** erzeugen.

#### Eintragen auf dem Server

Am Arbeitsplatz im Wurzelverzeichnis des Repos; das Skript spricht den Server über \`ssh office\` an:

\`\`\`sh
bash ${EINRICHTUNGSSKRIPT} gitlab <key> "<Label>" https://gitlab.meine-energie.de -
bash ${EINRICHTUNGSSKRIPT} youtrack <key> "<Label>" https://youtrack.meine-energie.de -
\`\`\`

Der \`key\` besteht aus a–z, 0–9 und „-". Das \`-\` am Ende heisst „für alle Unternehmen"; statt dessen gehen Kürzel mit Komma (\`BS,ME\`). Das Token fragt das Skript verdeckt ab — gib es nie als Argument mit. Es sichert die Konfiguration, prüft sie, startet den Dienst neu und schaltet den Abgleich \`external-tickets-sync.timer\` ein.

\`\`\`sh
# Rückschreiben der Fälligkeit an/aus (erst nach den Prüfpunkten unten):
bash ${EINRICHTUNGSSKRIPT} rueckschreiben <gitlab|youtrack> <key> an
# Abgleich alle 60 Minuten, Sperre für „Jetzt abgleichen" 120 s („-" = Vorgabe):
bash ${EINRICHTUNGSSKRIPT} fremdtickets 60 120
\`\`\`

#### Zuordnung durch die Nutzer

Jeder wählt sein Konto unter Einstellungen › **GitLab und YouTrack**. Selbst zuordnen geht nur, wenn die E-Mail des Kontos im Fremdsystem gleich der eigenen ist **und** diese im Login bestätigt ist (Keycloak: am Nutzer „Email verified" an, danach neu anmelden). Sonst ordnet die Verwaltung zu: Verwaltung › Benutzer, Block „GitLab und YouTrack".

#### Prüfpunkte vor dem Rückschreiben

1. Probelauf nach dem Eintragen endet mit 0: \`journalctl -u external-tickets-sync.service -n 20\`.
2. Eigenes Konto zuordnen: Die Zeile zeigt „n Fremdtickets", im Board steht links die Spalte „Fremdtickets".
3. Fälligkeit gegen das echte YouTrack: an einem Testvorgang ein Due Date setzen, den Rohwert lesen (\`GET /api/issues/<id>?fields=customFields(name,value)\`) und mit der Fremdspalte vergleichen. Weicht ein Tag ab: Rückschreiben aus lassen.
4. Feldauswahl YouTrack: Status, Zuständig, Due Date und Spent time kommen an. Heissen die Felder anders, beim Eintragen \`DUE_DATE_FIELD=…\` bzw. \`SPENT_TIME_FIELD=…\` setzen.
5. Erst dann Rückschreiben einschalten — mit GitLab-Scope \`api\` bzw. YouTrack „Update Issue" am Dienstkonto.

#### Freigegebene Hosts

Fremdtickets gibt es nur von \`gitlab.meine-energie.de\` und \`youtrack.meine-energie.de\`. Ein anderer Host taugt allenfalls für Verknüpfungen; zieht ein System um, braucht es ein Release. Ausführlich: \`deploy/README.md\`, Abschnitt „Tickets Nachtrag 1f".
`;

export type FremdsystemeEinrichtungProps = {
  /** Steht der Abschnitt beim Zeichnen offen? Im eigenen Hilfe-Eintrag ja, im Detail
   *  einer Instanz nein (dort ist der Zustand die Hauptsache). */
  startOffen: boolean;
};

/**
 * Der aufklappbare Abschnitt „Einrichtung" (me-abschnitt). Der Zustand gehört dem Baustein
 * selbst — anders als bei den Kopfblöcken der Masken gibt es hier keinen Speichern-Versuch,
 * der von aussen aufklappen müsste. Den Kontextwechsel löst der Aufrufer über `key`.
 * Eingeklappt heisst versteckt, nicht abgeräumt (Muster `Aufklapper`).
 */
export function FremdsystemeEinrichtung(p: FremdsystemeEinrichtungProps): ReactElement {
  const [offen, setOffen] = useState(p.startOffen);
  return (
    <Abschnitt className="schn__hilfe" titel="Einrichtung" offen={offen}
      onUmgeschaltet={(d) => setOffen(d.offen)}>
      <div className="schn__hilfetext" hidden={!offen}>{renderMarkdown(FREMDSYSTEME_HILFE)}</div>
    </Abschnitt>
  );
}
